import {
  Directive,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  inject,
  input,
  output,
} from '@angular/core';
import { fromEvent, merge, Subscription } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';

/**
 * Inactivity Detector Directive
 *
 * Detects user inactivity on an element and its children.
 * Emits an event when the user stops interacting for a specified delay.
 *
 * @example
 * <div cmInactivityDetector
 *      [inactivityDelay]="3000"
 *      (inactive)="onUserInactive()">
 *   <!-- content -->
 * </div>
 *
 * Monitored events: mousemove, mousedown, mouseup, keydown, keyup, scroll, touchstart,
 * touchmove, touchend, click, input, change, focus
 *
 * Implementation:
 * - Listens to events globally on document (capture phase)
 * - Filters to only events that occur within the directive's element
 * - This ensures all interactions are captured, even from deeply nested Angular/Kendo components
 */
@Directive({
  selector: '[cmInactivityDetector]',
  standalone: true,
})
export class InactivityDetectorDirective implements OnInit, OnDestroy {
  private readonly _elementRef = inject(ElementRef<HTMLElement>);
  private readonly _ngZone = inject(NgZone);
  /**
   * Delay in milliseconds before considering the user inactive
   * @default 3000
   */
  public readonly inactivityDelay = input<number>(3000);

  /**
   * Emits when the user becomes inactive
   */
  public readonly inactive = output<void>();

  private _subscription?: Subscription;

  public ngOnInit(): void {
    this._ngZone.runOutsideAngular(() => {
      const element = this._elementRef.nativeElement;

      // Listen to events globally on document to catch all interactions
      // This ensures we capture events from deeply nested components (Angular Material, Kendo, etc.)
      const options = { capture: true, passive: true };
      const mousemove$ = fromEvent(document, 'mousemove', options);
      const mousedown$ = fromEvent(document, 'mousedown', options);
      const mouseup$ = fromEvent(document, 'mouseup', options);
      const keydown$ = fromEvent(document, 'keydown', options);
      const keyup$ = fromEvent(document, 'keyup', options);
      const scroll$ = fromEvent(document, 'scroll', options);
      const touchstart$ = fromEvent(document, 'touchstart', options);
      const touchmove$ = fromEvent(document, 'touchmove', options);
      const touchend$ = fromEvent(document, 'touchend', options);
      const click$ = fromEvent(document, 'click', options);
      const input$ = fromEvent(document, 'input', options);
      const change$ = fromEvent(document, 'change', options);
      const focus$ = fromEvent(document, 'focus', options);

      // Merge all events, filter to only those within our element, then apply debounce
      this._subscription = merge(
        mousemove$,
        mousedown$,
        mouseup$,
        keydown$,
        keyup$,
        scroll$,
        touchstart$,
        touchmove$,
        touchend$,
        click$,
        input$,
        change$,
        focus$
      )
        .pipe(
          filter((event: Event) => element.contains(event.target)),
          debounceTime(this.inactivityDelay())
        )
        .subscribe(() => this._ngZone.run(() => this.inactive.emit()));
    });
  }

  public ngOnDestroy(): void {
    this._subscription?.unsubscribe();
  }
}
